import * as Sentry from '@sentry/vue'
import Util from '@/util'
import SelfPacedQuiz from '@/services/SelfPacedQuiz'
import storage from '@/services/storage'

const MESSAGE_TYPES = {
  PRESENTATION: 'presentation',
  CLEAR_PRESENTATION_DATA: 'slidePresentationData',
}

const ACTIONS = {
  NEXT_SLIDE: 'nextSlide',
  STOP_SUBMISSION_ACTION: 'stopSubmissionAction',
  BULLET_POINTS_INDEX_ACTION: 'bulletPointsIndexAction',
  LANGUAGE: 'language',
  CHANGE_ACCESS_CODE: 'changeAccessCode',
  AUDIENCE_PACING: 'audience-pacing',
  PRESENTING: 'presenting',
  UPDATE: 'update',
  FONT_FAMILY: 'fontFamily',
  QNA_ANONYMOUS: 'qnaAnonymous',
  QNA_ALL_SLIDE: 'qnaAllSlide',
  QNA_AUDIENCE_SHOW_ALL: 'qnaAudienceShowAll',
  UPDATE_LOGO: 'updateLogo',
  SHOW_HYPER_LINK: 'showHyperLink',
  PRIVATE_MODE: 'privateMode',
  NOT_SHOW_AHA_SLIDES_LOGO: 'notShowAhaSlidesLogo',
  HIDE_INDIVIDUAL_LEADERBOARD: 'hideIndividualLeaderboard',
  PRESENTATION_UPDATE: 'presentation-update',
  FILTERING_PROFANITY: 'filteringProfanity',
  UPDATE_LAST_SLIDE: 'updateLastSlide',
  ENABLE_MODERATION_MODE: 'enableModerationMode',
  OPEN_PRIVATE_FEEDBACK: 'open-private-feedback',
}

function nextSlideHandler(doc, rootComponent) {
  const { slide } = rootComponent.$store.state
  if (slide.id && rootComponent.$store.state.presentation.audiencePacing) {
    return
  }
  rootComponent.$store.state.idSlide = null
  if (doc.data.id == rootComponent.$store.state.slide.id) {
    if (doc.data.reload) {
      rootComponent.$router.go()
    } else {
      rootComponent.showConfirmNext = false
      rootComponent.dataSlideNext = null
    }
    return
  }
  if (rootComponent.currentDataNextSlide && rootComponent.currentDataNextSlide.timestamp > doc.data.timestamp) {
    return
  }
  rootComponent.$store.state.currentDataNextSlide = doc.data
  rootComponent.dataSlideNext = doc.data

  if (rootComponent.$store.state.slide.id === null) {
    rootComponent.confirmNextSlide()
  }

  const SLIDE_TYPES_TO_CONFIRM = [
    'textSlide',
    'bulletPoints',
    'freestyle',
    'qrcode',
    'image',
    'youtube',
    'question',
    'document',
    'googleSlides',
  ]

  if (
    SLIDE_TYPES_TO_CONFIRM.includes(rootComponent.$store.state.slide.type) ||
    rootComponent.$store.state.presentation.privateMode ||
    rootComponent.enableAudienceAdmissionForm()
  ) {
    rootComponent.confirmNextSlide()
    return
  }

  if (
    doc.data.type === 'pickAnswer' ||
    rootComponent.$store.state.slide.type === 'pickAnswer' ||
    doc.data.type === 'spinnerWheel' ||
    rootComponent.$store.state.slide.type === 'spinnerWheel' ||
    doc.data.type === 'ideas' ||
    rootComponent.$store.state.slide.type === 'ideas'
  ) {
    rootComponent.confirmNextSlide()
    rootComponent.dataSlideNext = null
    rootComponent.showConfirmNext = false
    return
  }

  if (rootComponent.$store.state.slide.type === 'leaderboard') {
    if (
      !rootComponent.$store.state.audienceName ||
      rootComponent.$store.state.slide.finalLeaderboard
    ) {
      rootComponent.showConfirmNext = true
      return
    }
    rootComponent.confirmNextSlide()
    return
  }

  // When submission is stopped, go to next slide immediately
  if (rootComponent.$store.state.slide.stopSubmission &&
    [
      'openEnded',
      'wordCloud',
      'multipleChoice',
      'imageChoice',
      'scale',
    ].indexOf(rootComponent.$store.state.slide.type) > -1
  ) {
    rootComponent.showConfirmNext = false
    rootComponent.confirmNextSlide()
    return
  }

  if (
    rootComponent.$store.state.slide.type === 'openEnded' &&
    rootComponent.$store.state.slide.openEndedSubmited &&
    ((rootComponent.$store.state.slide.submited && rootComponent.$store.state.slide.openEndedMultipleOption) ||
     !rootComponent.$store.state.slide.openEndedMultipleOption)
  ) {
    rootComponent.confirmNextSlide()
    return
  }

  if (
    rootComponent.$store.state.slide.type === 'wordCloud' &&
    !rootComponent.$store.state.slide.openEndedMultipleOption &&
    rootComponent.$store.state.slide.openEndedSubmited
  ) {
    rootComponent.confirmNextSlide()
    return
  }
  if (
    rootComponent.$store.state.slide.type === 'multipleChoice' ||
    rootComponent.$store.state.slide.type === 'imageChoice' ||
    rootComponent.$store.state.slide.type === 'scale'
  ) {
    if (rootComponent.$store.state.slide.multipleChoiceStatus) {
      rootComponent.confirmNextSlide()
      rootComponent.$store.state.slide.radioSelectded = 1
    } else if (
      rootComponent.dataSlideNext &&
      rootComponent.$store.state.slide.id != rootComponent.dataSlideNext.id
    ) {
      rootComponent.showConfirmNext = true
    }

    return
  }
  if (rootComponent.$store.state.slide.deleted) {
    rootComponent.confirmNextSlide()
    return
  }
  if (!rootComponent.$store.state.slide.radioSelectded) {
    rootComponent.showConfirmNext = true
  } else {
    rootComponent.confirmNextSlide()
  }
}

function changeAccessCodeHandler(doc, rootComponent) {
  rootComponent.$store.state.presentation.accessCode = doc.data.accessCode
}

function languageHandler(doc, rootComponent) {
  rootComponent.$store.state.presentation.language =
      doc.data.presentation.language
  rootComponent.$store.dispatch('setLang', doc.data.presentation.language)
}

function qnaAnonymousHandler(doc, rootComponent) {
  rootComponent.$store.state.presentation.qnaAnonymous =
      doc.data.presentation.qnaAnonymous
}

function presentingHandler(doc, rootComponent) {
  rootComponent.$store.state.presentation.presenting =
      doc.data.presentation.presenting
  if (doc.data.presentation.presenting) {
    rootComponent.sendRequestUpdateTimeAudience()
  }
}

function notShowAhaSlidesLogoHandler(doc, rootComponent) {
  rootComponent.$store.state.presentation.notShowAhaSlidesLogo =
  doc.data.presentation.notShowAhaSlidesLogo
}

function audiencePacingHandler(doc, rootComponent) {
  const { slide } = rootComponent.$store.state
  SelfPacedQuiz.clearSelfPaceTimeouts()
  rootComponent.$store.state.presentation.audiencePacing =
      doc.data.presentation.audiencePacing
  if (rootComponent.$store.state.presentation.audiencePacing) {
    rootComponent.getSlideList(rootComponent.$store.state.presentation.id, true)
  } else {
    window.location.reload()
    rootComponent.$store.state.slide.isEndSlide = false
  }
  rootComponent.$store.dispatch('updateSlide', slide)
}

function showHyperLinkHandler(doc, rootComponent) {
  rootComponent.$store.state.presentation.showHyperLink =
      doc.data.presentation.showHyperLink
}

function openPrivateFeedbackHandler(doc, rootComponent) {
  rootComponent.$router.push({ name: 'feedback' })
}

function enableModerationModeHandler(doc, rootComponent) {
  rootComponent.$store.state.presentation.isModerationMode = doc.data
  const arr = JSON.parse(JSON.stringify(rootComponent.$store.state.questions))
  Util.updateDataQuestionAnswer(
    arr,
    rootComponent.$store.state.tabQnaQuestion,
  )
}

function privateModeHandler(doc, rootComponent) {
  SelfPacedQuiz.clearSelfPaceTimeouts()
  rootComponent.$store.state.presentation.privateMode =
      doc.data.presentation.privateMode
  if (!doc.data.presentation.privateMode) {
    if (doc.data.presentation.audiencePacing) {
      window.location.reload()
    } else {
      // Khi presenter mở public trở lại, thì cần count số participants cho đúng
      // Tuy nhiên, cần tránh việc tất cả audience cùng gọi 1 API tại 1 thời điểm
      setTimeout(() => {
        rootComponent.trackAudienceJoinPresentation(rootComponent.audienceId, doc.data.presentation.id)
      }, Math.random() * 10000)
    }
  }
}

function updateLogoHandler(doc, rootComponent) {
  rootComponent.$store.state.logo = doc.data.presentation.logo
}

function filteringProfanityHandler(doc, rootComponent) {
  rootComponent.$store.state.presentation.filteringProfanity = doc.data.presentation.filteringProfanity
}

function qnaAllSlideHandler(doc, rootComponent) {
  rootComponent.$store.state.presentation.qnaAllSlide =
      doc.data.presentation.qnaAllSlide
}

function qnaAudienceShowAllHandler(doc, rootComponent) {
  rootComponent.$store.state.presentation.qnaAudienceShowAll =
      doc.data.presentation.qnaAudienceShowAll
  rootComponent.handleDataQuestions(rootComponent.$store.state.presentation)
}

function hideIndividualLeaderboardHandler(doc, rootComponent) {
  rootComponent.$store.state.presentation.isHideIndividualLeaderboard =
      doc.data.presentation.isHideIndividualLeaderboard
}

function fontFamilyHandler(doc, rootComponent) {
  rootComponent.$store.state.presentation.fontFamily =
      doc.data.presentation.fontFamily
  if (doc.data.presentation.fontFamily) {
    Util.setFontSlideV2(doc.data.presentation.fontFamily)
  }
}

function updateLastSlideHandler(doc, rootComponent) {
  rootComponent.$store.state.slide.isEndSlide = false
  rootComponent.$store.state.presentation.lastSlide = doc.data
  rootComponent.$store.dispatch('updateSlide', rootComponent.$store.state.slide)
  rootComponent._checkSubmited()
}

function compareSlides(newData, oldData) {
  if (newData.image
    && oldData.image
    && newData.image.name === oldData.image.name
  ) {
    newData.image = oldData.image
  }

  if (newData.backgroundImage
    && oldData.backgroundImage
    && newData.backgroundImage.name === oldData.backgroundImage.name
  ) {
    newData.backgroundImage = oldData.backgroundImage
  }
  return newData
}

function stopSubmissionHandler(doc, rootComponent) {
// Update dataSlideNext on start / stop submission
  if (!rootComponent.$store.state.slide.id || (rootComponent.$store.state.slide.id == doc.data.id)) {
    const url = doc.data.slideCacheUrl
    if (doc.action === 'bulletPointsIndexAction') {
      rootComponent.currentDataBulletPoint = doc.data
    }
    rootComponent.$http.get(url)
      .then((res) => {
        if (doc.action === 'bulletPointsIndexAction' && rootComponent.$store.state.slide.id != doc.data.id) {
          return
        }
        if (doc.action === 'bulletPointsIndexAction' && rootComponent.currentDataBulletPoint.timestamp > doc.data.timestamp) {
          return
        }
        const body = rootComponent.tryParseBody(res.bodyText, rootComponent.$store.state.presentation.id)
        const newSlideData = Util.formatSlide(body)
        rootComponent.$store.dispatch('updateSlide', compareSlides(newSlideData, rootComponent.$store.state.slide))
      })
      .catch((err) => {
        if (Sentry && err.status) {
          err.from = 'Audience update current slide'
          Sentry.captureException(JSON.stringify(err))
        }
      })
  } else if (rootComponent.dataSlideNext) {
    rootComponent.dataSlideNext = doc.data
  }
}

function bullerPointIndexHandler(doc, rootComponent) {
// Update dataSlideNext on start / stop submission
  if (!rootComponent.$store.state.slide.id || (rootComponent.$store.state.slide.id == doc.data.id)) {
    const url = doc.data.slideCacheUrl
    if (doc.action === 'bulletPointsIndexAction') {
      rootComponent.currentDataBulletPoint = doc.data
    }
    rootComponent.$http.get(url)
      .then((res) => {
        if (doc.action === 'bulletPointsIndexAction' && rootComponent.$store.state.slide.id != doc.data.id) {
          return
        }
        if (doc.action === 'bulletPointsIndexAction' && rootComponent.currentDataBulletPoint.timestamp > doc.data.timestamp) {
          return
        }
        const body = rootComponent.tryParseBody(res.bodyText, rootComponent.$store.state.presentation.id)
        const newSlideData = Util.formatSlide(body)
        rootComponent.$store.dispatch('updateSlide', compareSlides(newSlideData, rootComponent.$store.state.slide))
      })
      .catch((err) => {
        if (Sentry && err.status) {
          err.from = 'Audience update current slide'
          Sentry.captureException(JSON.stringify(err))
        }
      })
  } else if (rootComponent.dataSlideNext) {
    rootComponent.dataSlideNext = doc.data
  }
}

function presentationUpdateHandler(doc, rootComponent) {
  if (doc.data.teamPlay !== undefined) {
    rootComponent.$store.state.presentation.teamPlay = doc.data.teamPlay
    SelfPacedQuiz.playSelfPacedQuizSlide(rootComponent.$store.state.presentation, rootComponent.$store.state.slide)
  }
  if (doc.data.teamData !== undefined) {
    rootComponent.$store.state.presentation.teamData = doc.data.teamData
  }
  if (doc.data.teamSize !== undefined) {
    rootComponent.$store.state.presentation.teamSize = doc.data.teamSize
  }
  if (doc.data.teamCount !== undefined) {
    rootComponent.$store.state.presentation.teamCount = doc.data.teamCount
  }
  if (doc.data.teamScoringRule !== undefined) {
    rootComponent.$store.state.presentation.teamPlay = doc.data.teamScoringRule
  }
  if (doc.data.audienceAdmission !== undefined) {
    rootComponent._handleAudienceAdmissionDataFromSocket(doc.data)
  }
  if (doc.data.isEnableChat !== undefined) {
    rootComponent.$store.state.presentation.isEnableChat = doc.data.isEnableChat
  }
  if (doc.data.isEnablePrivateFeedback !== undefined) {
    rootComponent.$store.state.presentation.isEnablePrivateFeedback = doc.data.isEnablePrivateFeedback
  }
  if (doc.data.hideInstructionBar !== undefined) {
    rootComponent.$store.state.presentation.hideInstructionBar = doc.data.hideInstructionBar
  }
}

const actionHandlers = {
  [ACTIONS.NEXT_SLIDE]: nextSlideHandler,
  [ACTIONS.LANGUAGE]: languageHandler,
  [ACTIONS.CHANGE_ACCESS_CODE]: changeAccessCodeHandler,
  [ACTIONS.QNA_ANONYMOUS]: qnaAnonymousHandler,
  [ACTIONS.NOT_SHOW_AHA_SLIDES_LOGO]: notShowAhaSlidesLogoHandler,
  [ACTIONS.AUDIENCE_PACING]: audiencePacingHandler,
  [ACTIONS.PRESENTING]: presentingHandler,
  [ACTIONS.OPEN_PRIVATE_FEEDBACK]: openPrivateFeedbackHandler,
  [ACTIONS.ENABLE_MODERATION_MODE]: enableModerationModeHandler,
  [ACTIONS.PRIVATE_MODE]: privateModeHandler,
  [ACTIONS.HIDE_INDIVIDUAL_LEADERBOARD]: hideIndividualLeaderboardHandler,
  [ACTIONS.UPDATE_LOGO]: updateLogoHandler,
  [ACTIONS.SHOW_HYPER_LINK]: showHyperLinkHandler,
  [ACTIONS.QNA_ALL_SLIDE]: qnaAllSlideHandler,
  [ACTIONS.QNA_AUDIENCE_SHOW_ALL]: qnaAudienceShowAllHandler,
  [ACTIONS.FILTERING_PROFANITY]: filteringProfanityHandler,
  [ACTIONS.FONT_FAMILY]: fontFamilyHandler,
  [ACTIONS.PRESENTATION_UPDATE]: presentationUpdateHandler,
  [ACTIONS.UPDATE_LAST_SLIDE]: updateLastSlideHandler,
  [ACTIONS.STOP_SUBMISSION_ACTION]: stopSubmissionHandler,
  [ACTIONS.BULLET_POINTS_INDEX_ACTION]: bullerPointIndexHandler,

}

function handlePresentationMessages(doc, rootComponent) {
  const actionHandler = actionHandlers[doc.action]
  if (actionHandler && typeof actionHandler === 'function') {
    actionHandler(doc, rootComponent)
  }
}

function handleClearPresentationData(doc, rootComponent) {
  const { resetTimeStamp } = doc.data
  const presentationId = rootComponent.$store.state.presentation.id
  storage.clearValueByPresentation(presentationId)
  storage.setValueByPresentation(presentationId, 'resetTimeStamp', resetTimeStamp)
  rootComponent.clearChatMessagesAction({
    uniqueAccessCode: rootComponent.$store.state.presentation.uniqueAccessCode,
  })
  window.location.reload()
}

const PresentationWebSocketSubscriber = {
  [MESSAGE_TYPES.PRESENTATION]: handlePresentationMessages,
  [MESSAGE_TYPES.CLEAR_PRESENTATION_DATA]: handleClearPresentationData,
}

export default PresentationWebSocketSubscriber
